import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Check, CreditCard, Star, Zap, Crown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentModal = ({ isOpen, onClose, user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const subscriptionPlans = [
    {
      id: 'standard',
      name: 'Standard',
      price: '£15',
      period: '/month',
      icon: <Check className="h-6 w-6" />,
      color: 'bg-blue-500',
      features: [
        'Basic business listing',
        'Contact information display',
        'Basic search visibility',
        'Customer reviews',
        'Email support'
      ],
      popular: false
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '£45',
      period: '/month',
      icon: <Star className="h-6 w-6" />,
      color: 'bg-orange',
      features: [
        'Featured business listing',
        'Premium placement in search',
        'Photo gallery (up to 10 images)',
        'Social media integration',
        'Analytics dashboard',
        'Priority support'
      ],
      popular: true
    },
    {
      id: 'elite',
      name: 'Elite',
      price: '£500',
      period: '/month',
      icon: <Crown className="h-6 w-6" />,
      color: 'bg-amber',
      features: [
        'Sponsored listing placement',
        'Homepage banner advertising',
        'Unlimited photo gallery',
        'Video content support',
        'Advanced analytics',
        'Dedicated account manager',
        'API access',
        'White-label options'
      ],
      popular: false
    }
  ];

  const oneTimeServices = [
    {
      id: 'job_listing',
      name: 'Job Listing',
      price: '£45',
      period: '/listing',
      icon: <Zap className="h-6 w-6" />,
      description: 'Post a job opportunity for 30 days with premium visibility'
    },
    {
      id: 'featured_event',
      name: 'Featured Event',
      price: '£75',
      period: '/event',
      icon: <Star className="h-6 w-6" />,
      description: 'Promote your event with featured placement and highlighting'
    }
  ];

  const handlePayment = async (packageId) => {
    if (!user) {
      setError('Please log in to continue with payment');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Payment processing failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold text-charcoal mb-2">
            Choose Your Plan
          </DialogTitle>
          <p className="text-center text-gray-600">
            Boost your business visibility and connect with more customers
          </p>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {/* Subscription Plans */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 text-charcoal">Monthly Subscriptions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {subscriptionPlans.map((plan) => (
                <Card 
                  key={plan.id} 
                  className={`relative transition-all duration-200 hover:shadow-lg ${
                    plan.popular ? 'border-orange scale-105 shadow-lg' : 'border-gray-200'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-orange text-white px-3 py-1">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <div className={`${plan.color} w-12 h-12 rounded-full flex items-center justify-center text-white mx-auto mb-3`}>
                      {plan.icon}
                    </div>
                    <CardTitle className="text-xl text-charcoal">{plan.name}</CardTitle>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-charcoal">{plan.price}</span>
                      <span className="text-gray-600">{plan.period}</span>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button
                      onClick={() => handlePayment(plan.id)}
                      disabled={isLoading}
                      className={`w-full ${
                        plan.popular 
                          ? 'bg-orange hover:bg-orange/90' 
                          : 'bg-primary hover:bg-primary/90'
                      }`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {isLoading ? 'Processing...' : `Get ${plan.name}`}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* One-time Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-charcoal">One-time Services</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {oneTimeServices.map((service) => (
                <Card key={service.id} className="border-gray-200 hover:shadow-lg transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="bg-primary w-10 h-10 rounded-full flex items-center justify-center text-white">
                          {service.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-charcoal">{service.name}</h4>
                          <div className="text-primary font-bold">
                            {service.price}
                            <span className="text-gray-600 font-normal">{service.period}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    
                    <Button
                      onClick={() => handlePayment(service.id)}
                      disabled={isLoading}
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-primary hover:text-white"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {isLoading ? 'Processing...' : `Purchase ${service.name}`}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="text-center text-sm text-gray-500 border-t pt-4">
            <p>All payments are secure and processed via Stripe. Cancel anytime for monthly subscriptions.</p>
            <p className="mt-1">Need help? Contact our support team for personalized assistance.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;